import type { PartialSearchResponse } from "src/domain/TripPlanner/util/getPartialSearchResponse";
import { getSingleMajorFlightSegment } from "src/utils/getSingleMajorFlightSegment";
import { createTripHashForCard } from "src/utils/location/createTripHashForCard";
import type { SyntheticEvent } from "react";
import { transitModeFromSegment } from "src/utils/adapters/transitMode";
import type { Mode } from "../../../../utils/types/mode";
import type { Place, SearchResponse } from "../../../../api/SearchResponse";
import { durationInMinutesFromRoute } from "../../../../utils/adapters/duration";
import {
  priceRangeFromRoute,
  type PriceRange,
} from "../../../../utils/adapters/priceRange";
import type {
  TripPlannerCardType,
  TripPlannerURL,
} from "../../../../domain/TripPlanner/TripPlannerProvider";

type TripOriginDestination = {
  origin: Place;
  destination: Place;
};

type TripCardTypeProps = {
  type: TripPlannerCardType;
  transitMode: Mode;
  url: {
    pathname: string;
    hash: string | undefined;
  };
  searchResponse?: SearchResponse | PartialSearchResponse;
  priceRange?: PriceRange;
  majorModes?: Mode[];
  durationInMinutes?: number;
  clickCallback?: () => void;
  gaLabel?: string;
  isScheduleable?: boolean;
  ContainerOverride?: React.ComponentType<
    React.PropsWithChildren<{
      isPromptLike: boolean;
      isTripsAsCoreHoldback?: boolean;
      onClick?: (event: SyntheticEvent) => void;
    }>
  >;
  modeSummary?: Mode[] | [];
  index?: number;
} & TripOriginDestination;

export type RouteTripCardProps = {
  title?: string;
  majorModes?: Mode[];
} & TripCardTypeProps;

export function getRoutePropsFromSearchResponse(
  searchResponse: SearchResponse | PartialSearchResponse,
  routeIndex: number,
  url: TripPlannerURL,
): RouteTripCardProps {
  const routeData = searchResponse.routes[routeIndex];
  const title = routeData.name;
  const transitModes = getTripRouteTransitModes(searchResponse, routeIndex);
  const primaryMode = getPrimaryTransitMode(transitModes);
  const durationInMinutes = durationInMinutesFromRoute(
    searchResponse,
    routeData,
  );
  const isScheduleable =
    !!routeData.scheduleInfo ||
    getSingleMajorFlightSegment(searchResponse, routeIndex) !== undefined;

  return {
    origin: searchResponse.places[0],
    destination: searchResponse.places[1],
    title,
    searchResponse,
    durationInMinutes,
    priceRange: priceRangeFromRoute(routeData),
    majorModes: transitModes,
    transitMode: primaryMode,
    url: {
      ...url,
      hash: createTripHashForCard(searchResponse, url.hash),
    },
    type: "route",
    gaLabel: getTripCardAnalyticsLabel(false, searchResponse, routeIndex),
    isScheduleable,
  };
}

function getPrimaryTransitMode(transitModes: Mode[]) {
  return transitModes.length > 1 ? "multi" : transitModes[0];
}

function getTripRouteTransitModes(
  searchResponse: SearchResponse | PartialSearchResponse,
  routeIndex: number,
): Mode[] {
  const route = searchResponse.routes[routeIndex];
  const segmentModes = route.segments.map((segment, i) => {
    const isMajor = searchResponse.segments[segment].isMajor;
    return isMajor
      ? transitModeFromSegment(searchResponse, routeIndex, i)
      : false;
  });
  return segmentModes.filter((mode): mode is Mode => Boolean(mode));
}

export function getTripCardAnalyticsLabel(
  overwrite: boolean,
  searchResponse: SearchResponse | PartialSearchResponse,
  routeIndex?: number,
  segmentIndex?: number,
) {
  const label: (string | number)[] = ["tc0"];
  if (overwrite) {
    label.push("Override");
  }
  const origin = searchResponse.places[0];
  const destination = searchResponse.places[1];

  if (origin && origin.canonicalName) {
    label.push(origin.canonicalName);
  }
  if (destination && destination.canonicalName) {
    label.push(destination.canonicalName);
  }

  if (typeof routeIndex === "number" && !isNaN(routeIndex)) {
    label.push(searchResponse.routes[routeIndex].canonicalName ?? routeIndex);
  }

  if (typeof segmentIndex === "number" && !isNaN(segmentIndex)) {
    label.push(segmentIndex);
  }

  return label.join(":");
}
